import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { NgApexchartsModule } from 'ng-apexcharts';
import type {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
} from 'ng-apexcharts';
import { DashboardService } from '@features/dashboard/services/dashboard.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';
import { DashboardCharts } from '@features/dashboard/models/dashboard.model';
import {
  BUSINESS_STATUS_METADATA,
  BusinessStatus,
} from '@shared/models/analysis.model';

/** One row each — an analysis still moving through the sales funnel. */
const OPEN_STATUSES: BusinessStatus[] = [
  'pending',
  'sent',
  'interested',
  'in_touch',
];
/** Mutually exclusive end states — share a single row, split by colour. */
const FINAL_STATUSES: BusinessStatus[] = ['won', 'lost', 'closed'];
const FINAL_ROW_LABEL = 'Abschluss';

interface ChartOptions {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  fill: ApexFill;
  tooltip: ApexTooltip;
  legend: ApexLegend;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  colors: string[];
}

/** A funnel row: label, count, share of all analyses, and its bar segments. */
interface FunnelRow {
  label: string;
  count: number;
  share: string;
  /** Widths are relative to the largest row, so short rows stay readable. */
  segments: { color: string; percent: number }[];
}

/**
 * ApexCharts writes colors into SVG fill/stroke attributes, which don't resolve
 * `var(--chart-1)` — so the theme tokens have to be read out of the document
 * once and handed over as literal values. Read at construction, i.e. the
 * palette follows whichever theme was active when the dashboard mounted.
 */
function cssToken(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

@Component({
  selector: 'app-dashboard-charts',
  standalone: true,
  imports: [NgApexchartsModule],
  templateUrl: './dashboard-charts.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardChartsComponent implements OnInit {
  private dashboardService = inject(DashboardService);
  private currentOrgStore = inject(CurrentOrganizationStore);

  private data = signal<DashboardCharts | null>(null);
  isLoading = signal(true);
  loadError = signal(false);

  private palette = {
    created: cssToken('--chart-1'),
    completed: cssToken('--chart-4'),
    border: cssToken('--border'),
    muted: cssToken('--muted-foreground'),
    /** Funnel ramp: the further along the funnel, the deeper the blue. */
    open: cssToken('--ks-grey-300') || cssToken('--border'),
    ramp: [
      cssToken('--chart-1'),
      cssToken('--chart-2'),
      cssToken('--chart-3'),
    ],
    final: {
      won: cssToken('--ks-success'),
      lost: cssToken('--ks-danger'),
      closed: cssToken('--ks-grey-500'),
    } as Record<BusinessStatus, string>,
  };

  private labelStyle = {
    fontSize: '11px',
    fontFamily: cssToken('--font-mono'),
    colors: cssToken('--muted-foreground'),
  };

  /** Rendered under the Abschluss row, which is the only multi-colour bar. */
  finalStatusLegend = FINAL_STATUSES.map((status) => ({
    label: BUSINESS_STATUS_METADATA[status].label,
    color: this.palette.final[status],
  }));

  timelineLegend = [
    { label: 'Erstellt', color: this.palette.created },
    { label: 'Abgeschlossen', color: this.palette.completed },
  ];

  async ngOnInit(): Promise<void> {
    const orgId = this.currentOrgStore.organization()?.id;
    if (!orgId) {
      this.isLoading.set(false);
      return;
    }
    try {
      this.data.set(await this.dashboardService.getCharts(orgId));
    } catch (error) {
      console.error('[DashboardCharts] Failed to load chart data:', error);
      this.loadError.set(true);
    } finally {
      this.isLoading.set(false);
    }
  }

  statusTotal = computed(() =>
    (this.data()?.businessStatus ?? [])
      .reduce((sum, r) => sum + r.count, 0)
      .toLocaleString('de-DE'),
  );

  funnelRows = computed<FunnelRow[]>(() => {
    const rows = this.data()?.businessStatus;
    if (!rows?.length) return [];

    const counts = new Map(rows.map((r) => [r.status, r.count]));
    const count = (status: BusinessStatus) => counts.get(status) ?? 0;
    const total = rows.reduce((sum, r) => sum + r.count, 0) || 1;
    const finalTotal = FINAL_STATUSES.reduce((sum, s) => sum + count(s), 0);
    const max = Math.max(...OPEN_STATUSES.map(count), finalTotal, 1);

    const share = (n: number) => `${Math.round((n / total) * 100)}%`;

    const openRows = OPEN_STATUSES.map((status, i) => ({
      label: BUSINESS_STATUS_METADATA[status].label,
      count: count(status),
      share: share(count(status)),
      segments: [
        {
          color: i === 0 ? this.palette.open : this.palette.ramp[i - 1],
          percent: (count(status) / max) * 100,
        },
      ],
    }));

    // The terminal statuses are outcomes of the same step, so they stay on one
    // row and are told apart by colour plus the legend underneath.
    return [
      ...openRows,
      {
        label: FINAL_ROW_LABEL,
        count: finalTotal,
        share: share(finalTotal),
        segments: FINAL_STATUSES.filter((s) => count(s) > 0).map((s) => ({
          color: this.palette.final[s],
          percent: (count(s) / max) * 100,
        })),
      },
    ];
  });

  timelineOptions = computed<ChartOptions | null>(() => {
    const timeline = this.data()?.timeline;
    if (!timeline?.length) return null;

    return {
      series: [
        { name: 'Erstellt', data: timeline.map((p) => p.created) },
        { name: 'Abgeschlossen', data: timeline.map((p) => p.completed) },
      ],
      chart: {
        type: 'area',
        height: 220,
        fontFamily: 'inherit',
        toolbar: { show: false },
        zoom: { enabled: false },
        parentHeightOffset: 0,
      },
      xaxis: {
        categories: timeline.map((p) => this.formatDay(p.date)),
        tickAmount: 5,
        labels: { rotate: 0, style: this.labelStyle },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: { labels: { style: this.labelStyle }, tickAmount: 3 },
      stroke: { curve: 'smooth', width: 2 },
      fill: {
        type: 'gradient',
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.18,
          opacityTo: 0.02,
          stops: [0, 90, 100],
        },
      },
      tooltip: { theme: 'light' },
      legend: { show: false },
      dataLabels: { enabled: false },
      // Horizontal rules only — they carry the y-scale, vertical ones would
      // just add noise over a 30-day span.
      grid: {
        show: true,
        borderColor: this.palette.border,
        xaxis: { lines: { show: false } },
        padding: { left: 0, right: 0, top: 0, bottom: 0 },
      },
      colors: [this.palette.created, this.palette.completed],
    };
  });

  /** '2026-08-09' → '09.08.' */
  private formatDay(isoDate: string): string {
    const [, month, day] = isoDate.split('-');
    return `${day}.${month}.`;
  }
}
